/**
 * The upgrade card (#1328).
 *
 * ⚠️ It says what Plus ADDS, and says out loud what free already covers —
 * specifically that allergies and medical diets are free forever. A paywall
 * that leaves somebody unsure whether they can record an allergy has done harm
 * even if it never blocked them.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { twoPlates } from "../api/twoPlates";
import { ApiError } from "../lib/api";

export function Upgrade({ prompt }: { prompt?: string }) {
  const { data } = useQuery({ queryKey: ["billing"], queryFn: twoPlates.billing });

  const checkout = useMutation({
    mutationFn: twoPlates.checkout,
    onSuccess: (res) => { window.location.href = res.url; },
  });
  const portal = useMutation({
    mutationFn: twoPlates.portal,
    onSuccess: (res) => { window.location.href = res.url; },
  });

  if (!data) return null;

  if (data.tier === "plus") {
    return (
      <section className="border-line rounded-xl border p-5">
        <h2 className="font-semibold">Two Plates Plus</h2>
        <p className="text-muted mt-1 text-sm">
          {data.status === "past_due"
            ? "Your last payment did not go through. Nothing has been taken away — update your card when you get a moment."
            : "Up to eight people, plans up to seven days, and as many diets as you need."}
        </p>
        {data.currentPeriodEnd && (
          <p className="text-muted mt-2 text-xs">
            Renews {new Date(data.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
          </p>
        )}
        <button
          onClick={() => portal.mutate()}
          disabled={portal.isPending}
          className="text-plate-b mt-3 text-sm underline underline-offset-4 disabled:opacity-50"
        >
          Manage or cancel
        </button>
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border p-5"
      style={{ borderColor: "var(--color-plate-b)" }}
    >
      {prompt && <p className="text-plate-b mb-3 text-sm font-medium">{prompt}</p>}

      <h2 className="font-semibold">Two Plates Plus — $39 a year</h2>
      <ul className="text-muted mt-3 flex flex-col gap-1.5 text-sm">
        <li>· Up to eight people, not two</li>
        <li>· Plans up to seven days, for one shop a week</li>
        <li>· As many chosen diets per person as you need</li>
      </ul>

      {/* Said here, unprompted, because somebody deciding whether to pay should
          never wonder whether safety is on the other side of the wall. */}
      <p className="mt-4 text-xs" style={{ color: "var(--color-plate-a)" }}>
        Allergies and medical diets are free, on every plan, always. You will never
        be asked to pay to tell us what makes somebody ill.
      </p>

      {!data.configured ? (
        <p className="text-muted mt-4 text-xs">Subscriptions are not switched on yet.</p>
      ) : (
        <div className="mt-5 flex flex-col gap-2.5">
          <button
            onClick={() => checkout.mutate("year")}
            disabled={checkout.isPending}
            className="bg-plate-b text-ground tap w-full rounded-lg font-semibold disabled:opacity-50"
          >
            $39 a year
          </button>
          <button
            onClick={() => checkout.mutate("month")}
            disabled={checkout.isPending}
            className="border-line tap w-full rounded-lg border text-sm font-medium disabled:opacity-50"
          >
            $4.99 a month
          </button>
        </div>
      )}

      {checkout.isError && (
        <p role="alert" className="text-warn mt-3 text-sm">
          {checkout.error instanceof ApiError ? checkout.error.message : "Could not start checkout"}
        </p>
      )}
    </section>
  );
}

/** Inline nudge for a control somebody just bounced off. */
export function UpgradeNote({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || !error.needsUpgrade) return null;
  return (
    <p role="alert" className="mt-2 text-sm" style={{ color: "var(--color-plate-b)" }}>
      {error.message}
    </p>
  );
}
